import { Divider, Group } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseGroupsSwitch } from '../../../components/use-groups-switch/use-groups-switch';
import { MappedPhasesProvider } from '../../../contexts/mapped-phases.context.tsx';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting.ts';
import { safeParseDateInput } from '../../../helpers/date-time-helpers/parsing/parsing.ts';
import { exportChart } from '../../../helpers/image-downloader/image-downloader.ts';
import { extractPhaseKeys } from '../../../helpers/subgroup-helpers/subgroup-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store.ts';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store.ts';
import { baseWhite, secondaryBase } from '../../../styles/design-tokens.ts';
import { Button } from '../../../ui-library/button/button.tsx';
import { Icon } from '../../../ui-library/icon/icon.tsx';
import { Select } from '../../../ui-library/select/select.tsx';
import { Text } from '../../../ui-library/typography/typography.tsx';
import { FloraButton } from '../../flora/flora-button.tsx';
import { getPhasesMetadata } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.constants';
import type { Phase } from '../../strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.type.ts';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { NoWorkPeriodSelected } from '../components/no-work-period-selected.tsx';
import { WorkPeriodChartHeader } from '../components/work-period-chart-header.tsx.tsx';
import {
  formatFlowByPhaseDataForFlora,
  formatTaskFlowDataForFlora,
} from '../helpers/single-assessment-flora.helpers.ts';
import { useFlowByPhaseData, useWorkPeriodsTaskFlowData } from '../hooks/work-periods-calculation.hooks.ts';
import { useHasSelection } from '../hooks/work-periods.hooks.ts';
import { TaskFlow } from './task-flow.tsx';
import { WorkPhaseDistributionChart } from './work-phase-distribution-chart.tsx';

interface WorkPhaseDistributionProps {
  onTasksChange: (tasks: TaskRecord[], title?: string, isLoading?: boolean) => void;
  tasksOpen: boolean;
}

export const WorkPhaseDistribution = ({ onTasksChange, tasksOpen }: WorkPhaseDistributionProps) => {
  const i18n = useTranslation();
  const { selectedWorkPeriods, useGroups, setUseGroups, selectedMetric } = useWorkPeriodStore();
  const selectedWorkPeriod = useMemo(() => selectedWorkPeriods[0], [selectedWorkPeriods]);
  const [viewType, setViewType] = useState<'graph' | 'flow'>('graph');
  const useAI = useGlobalStore((state) => state.useAI);

  const { data: flowData, phases: flowPhases, isLoading: isLoadingFlow } = useFlowByPhaseData();
  const { data: taskFlowData } = useWorkPeriodsTaskFlowData();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [visiblePhases, setVisiblePhases] = useState<Record<Phase, boolean>>({} as Record<Phase, boolean>);
  const downloadRef = useRef<HTMLDivElement>(null!);

  // Extract available phases from the API response and update visiblePhases
  // This ensures only phases with status mappings are shown in the legend
  const allPhasesMetadata = useMemo(() => getPhasesMetadata(i18n.t), [i18n.t]);
  const availablePhasesFromApi = useMemo(() => {
    if (!useGroups || !flowData) return null;
    const keys = extractPhaseKeys(flowData);
    const validPhaseIds = allPhasesMetadata.map((p) => p.id);
    return keys.filter((key) => validPhaseIds.includes(key as Phase)) as Phase[];
  }, [useGroups, flowData, allPhasesMetadata]);

  // Initialize visiblePhases when available phases change
  useEffect(() => {
    if (!useGroups) return;

    if (availablePhasesFromApi && availablePhasesFromApi.length > 0) {
      // Only include phases that are available from the API
      const newVisiblePhases: Record<Phase, boolean> = {} as Record<Phase, boolean>;
      availablePhasesFromApi.forEach((phase) => {
        newVisiblePhases[phase] = true;
      });
      setVisiblePhases(newVisiblePhases);
    } else if (!flowData) {
      // Default to all phases while loading (backward compatibility)
      setVisiblePhases({
        backlog: true,
        blocked: true,
        wip: true,
        review: true,
        test: true,
        deployable: true,
        done: true,
      } as Record<Phase, boolean>);
    }
  }, [useGroups, availablePhasesFromApi, flowData]);

  // Determine if we have a selected work period or date range
  const hasSelection = useHasSelection();

  const dates = useMemo(() => {
    return Object.keys(flowData || {});
  }, [flowData]);
  const latestDateFromChartData = useMemo(() => {
    // Find the maximum (latest) valid date in the dates array
    if (!dates || dates.length === 0) return null;

    // Filter out invalid dates and parse them
    const parsedDates = dates.map((date) => safeParseDateInput(date)).filter((d) => d !== null);

    if (parsedDates.length === 0) return null;

    // Find the latest date using dayjs's isAfter
    let latest = parsedDates[0];
    for (let i = 1; i < parsedDates.length; i++) {
      if (parsedDates[i] && parsedDates[i].isAfter(latest)) {
        latest = parsedDates[i];
      }
    }

    // If the latest date is in the future, return the current date
    return latest.isAfter(dayjs()) ? dayjs() : latest;
  }, [dates]);

  // Create date options for the select component
  const dateOptions = useMemo(() => {
    if (!dates || dates.length === 0) {
      return [];
    }

    const options = dates
      .filter((date) => {
        // Filter out invalid dates
        const parsedDate = safeParseDateInput(date);
        return parsedDate !== null;
      })
      .sort((a, b) => {
        const dateA = safeParseDateInput(a);
        const dateB = safeParseDateInput(b);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB.diff(dateA);
      })
      .map((date) => ({
        value: formatDate(date, { format: 'YYYY-MM-DD' }),
        label: formatDate(date, { format: 'MMM D, YYYY' }),
      }));

    return options;
  }, [dates]);

  // Set default selectedDate - prioritize work period end date, then latest chart data
  useEffect(() => {
    // Take the latest date from the chart data if the work period is active or in the future
    if (selectedWorkPeriod?.end_date && dayjs(selectedWorkPeriod.end_date).isAfter(dayjs())) {
      setSelectedDate(latestDateFromChartData ? formatDate(latestDateFromChartData, { format: 'YYYY-MM-DD' }) : null);
    }
    // otherwise take the latest date from the chart data
    else if (latestDateFromChartData) {
      const latestDate = formatDate(latestDateFromChartData, { format: 'YYYY-MM-DD' });
      setSelectedDate(latestDate);
    }
    // otherwise take the work period end date
    else if (selectedWorkPeriod?.end_date) {
      const workPeriodEndDate = formatDate(selectedWorkPeriod.end_date, { format: 'YYYY-MM-DD' });
      setSelectedDate(workPeriodEndDate);
    }
  }, [selectedWorkPeriod, latestDateFromChartData]);

  // Ensure selectedDate matches one of the available options when dateOptions changes
  useEffect(() => {
    if (dateOptions.length > 0) {
      // Use a ref or callback to get current selectedDate without dependency
      setSelectedDate((currentSelectedDate) => {
        if (!currentSelectedDate) {
          // If no date selected, pick the latest
          const latestOption = dateOptions[dateOptions.length - 1];
          return latestOption?.value || null;
        }

        const matchesOption = dateOptions.some((opt) => opt.value === currentSelectedDate);
        if (!matchesOption) {
          // If selectedDate doesn't match any option, select the latest available date
          const latestOption = dateOptions[dateOptions.length - 1];
          return latestOption?.value || currentSelectedDate;
        }

        return currentSelectedDate;
      });
    }
  }, [dateOptions]);

  const floraData = useMemo(
    () => formatFlowByPhaseDataForFlora(flowData, selectedDate, selectedMetric, selectedWorkPeriod, i18n.t),
    [flowData, selectedDate, selectedMetric, selectedWorkPeriod, i18n.t],
  );

  const taskFlowFloraData = useMemo(
    () => formatTaskFlowDataForFlora(taskFlowData, selectedDate, selectedMetric, selectedWorkPeriod, i18n.t),
    [taskFlowData, selectedDate, selectedMetric, selectedWorkPeriod, i18n.t],
  );

  const handlePhaseToggle = (phaseId: Phase) => {
    setVisiblePhases((prev) => ({
      ...prev,
      [phaseId]: !prev[phaseId],
    }));
  };

  const handleExport = () => {
    if (!downloadRef.current) return;
    exportChart(downloadRef.current, 'work-phase-distribution.png');
  };

  if (!hasSelection) {
    return <NoWorkPeriodSelected />;
  }

  return (
    <div ref={downloadRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WorkPeriodChartHeader
        title="Work Phase Distribution"
        selectedWorkPeriod={selectedWorkPeriod}
        onExport={handleExport}
        floraButton={
          useAI && (
            <FloraButton
              externalData={[viewType === 'graph' ? floraData : taskFlowFloraData]}
              prompt={i18n.t(
                viewType === 'graph'
                  ? 'flora.work_periods.work_phase_distribution_chart_prompt'
                  : 'flora.work_periods.task_flow_prompt',
              )}
              tooltipText={i18n.t('flora.work_periods.flora_button_tooltip')}
            />
          )
        }
      />
      <Group justify="space-between" align="center" mt={16} mb={16}>
        <Text weight="bold">{viewType === 'graph' ? 'Phase Distribution' : 'Phase Flow'}</Text>
        <Group gap="xs">
          {viewType === 'graph' && (
            <>
              <UseGroupsSwitch checked={useGroups} onChange={(checked) => setUseGroups(checked)} />
              <Divider orientation="vertical" />
            </>
          )}
          <Group>
            <Button
              radius="xl"
              size="xs"
              variant={viewType === 'graph' ? 'secondary' : 'outline'}
              onClick={() => setViewType('graph')}
              leftSection={
                <Icon
                  name={viewType === 'graph' ? 'stacked_line_chart' : 'bar_chart'}
                  color={viewType === 'graph' ? baseWhite : secondaryBase}
                  size={16}
                />
              }
            >
              Graph View
            </Button>
            <Button
              radius="xl"
              size="xs"
              variant={viewType === 'flow' ? 'secondary' : 'outline'}
              onClick={() => setViewType('flow')}
              leftSection={
                <Icon
                  name={viewType === 'flow' ? 'stacked_line_chart' : 'bar_chart'}
                  color={viewType === 'flow' ? baseWhite : secondaryBase}
                  size={16}
                />
              }
            >
              {`${i18n.t('common.work_unit')} Flow`}
            </Button>
          </Group>
          <Divider orientation="vertical" />
          <Select
            value={selectedDate}
            options={dateOptions}
            onChange={setSelectedDate}
            placeholder={dateOptions.length === 0 ? 'No dates available' : 'Select date'}
            size="md"
            radius="xl"
          />
        </Group>
      </Group>

      {viewType === 'graph' && (
        <WorkPhaseDistributionChart
          selectedWorkPeriod={selectedWorkPeriod}
          selectedDate={selectedDate}
          visiblePhases={visiblePhases}
          tasksOpen={tasksOpen}
          onTasksChange={onTasksChange}
          onPhaseToggle={handlePhaseToggle}
          useGroups={useGroups}
          flowData={flowData}
          phases={flowPhases}
        />
      )}
      {viewType === 'flow' && (
        <MappedPhasesProvider flowData={flowData} phases={flowPhases} isLoading={isLoadingFlow}>
          <TaskFlow selectedDate={selectedDate} />
        </MappedPhasesProvider>
      )}
    </div>
  );
};
